import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NgbDropdownModule, NgbModal, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { format } from 'date-fns';
import { Subject, takeUntil } from 'rxjs';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { SortHeaderComponent } from '../../../../../_shared/components/sort-header/sort-header.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ConfirmationModalService } from '../../../../../_shared/services/modals/confirmation-modal.service';
import { ToastrService } from 'ngx-toastr';
import { PartsRequestService } from '../../services/parts-request.service';
import {
  PartRequest,
  PartRequestActionState,
  PartRequestItem,
  PartRequestStatusVariant,
  PartsRequestFilterRequest,
  PartsRequestSortColumn,
  RequestStatusSummary,
  SortDirection
} from '../../models/part-request.model';
import { PartRequestStatus } from '../../enums/part-request-status.enum';
import { RejectPartComponent } from '../modals/reject-part/reject-part.component';
import { RejectPartResult } from '../../models/part-reject.modal';
import { ViewPartNotesComponent } from '../modals/view-part-notes/view-part-notes.component';
import { IssuePartsModalComponent } from '../modals/issue-parts-modal/issue-parts-modal.component';
import { IssuePartsMode, IssuePartsWorkOrderData } from '../../models/part-request.model';
import { ApprovePartsModalComponent } from '../modals/approve-parts-modal/approve-parts-modal.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-parts-request-overview',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule, NgbPopoverModule, SearchComponent, EmptyStateComponent, SortHeaderComponent, ErrorStateComponent, ViewPartNotesComponent, TranslocoDirective],
  templateUrl: './parts-request-overview.component.html',
  styleUrl: './parts-request-overview.component.scss'
})
export class PartsRequestOverviewComponent implements OnInit, OnDestroy {
  private readonly pageSize: number = 20;
  private readonly workOrderTooltipThreshold = 14;
  private readonly assetTooltipThreshold = 12;
  private readonly requestedByTooltipThreshold = 16;
  private readonly partNumberTooltipThreshold = 14;
  private readonly partNameTooltipThreshold = 18;
  private readonly categoryTooltipThreshold = 14;

  private errorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);
  private partsRequestService = inject(PartsRequestService);
  private modalService = inject(NgbModal);
  private confirmationModalService = inject(ConfirmationModalService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);

  displayedRequests: PartRequest[] = [];
  expandedRows: Set<number> = new Set<number>();
  selectedWorkOrders: Set<number> = new Set<number>();
  processingPartIds: Set<number> = new Set<number>();
  rejectingPartIds: Set<number> = new Set<number>();
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  isRefreshing: boolean = false;
  hasError: boolean = false;

  public searchTerm: string = '';
  public selectedCategory: string = 'All';
  public sortColumn: PartsRequestSortColumn | null = null;
  public sortDirection: SortDirection = null;
  public categoryOptions: string[] = [
    'All',
    'Pending',
    'Approved',
    'Rejected'
  ];

  private widgetFilter: WidgetFilterData = {} as WidgetFilterData;
  private currentPage: number = 1;
  private totalPages: number = 0;
  private hasMoreData: boolean = true;
  private requestSequence: number = 0;
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private destroySubject: Subject<void> = new Subject<void>();
  private loadCancellation: Subject<void> = new Subject<void>();
  private refreshCancellation: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;

    if (this.widgetFilter && !Object.values(this.widgetFilter).every(value => value === null)) {
      void this.resetAndLoadRequestsAsync();
    }

    this.filterService.filterUpdated$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((filter) => {
        if (!filter || Object.values(filter).every(value => value === null)) {
          return;
        }

        this.widgetFilter = filter;
        void this.resetAndLoadRequestsAsync();
      });
  }

  ngOnDestroy(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    this.destroySubject.next();
    this.destroySubject.complete();
  }

  loadMoreItems(): void {
    void this.loadRequestsPageAsync(false);
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollPosition = element.scrollTop + element.clientHeight;
    const scrollHeight = element.scrollHeight;

    if (scrollPosition >= scrollHeight * 0.8 && !this.isLoadingMore && this.hasMoreData) {
      this.loadMoreItems();
    }
  }

  onSearchTermUpdate(searchTerm: string): void {
    this.searchTerm = searchTerm;

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    this.searchDebounceTimer = setTimeout(() => {
      void this.resetAndLoadRequestsAsync();
    }, 300);
  }

  getStatusLabel(category: string): string {
    switch (category) {
      case 'Pending':
        return this.transloco.translate('parts.request.filters.pending');
      case 'Approved':
        return this.transloco.translate('parts.request.filters.approved');
      case 'Rejected':
        return this.transloco.translate('parts.request.filters.rejected');
      default:
        return this.transloco.translate('parts.request.filters.all');
    }
  }

  onStatusFilterChange(category: string): void {
    this.selectedCategory = category;
    void this.resetAndLoadRequestsAsync();
  }

  onSort(column: string): void {
    const nextDirection: SortDirection =
      this.sortColumn !== column || !this.sortDirection
        ? 'asc'
        : this.sortDirection === 'asc'
          ? 'desc'
          : null;

    this.sortColumn = nextDirection ? (column as PartsRequestSortColumn) : null;
    this.sortDirection = nextDirection;
    void this.resetAndLoadRequestsAsync();
  }

  onIssuePart(request?: PartRequest): void {
    if (request) {
      const workOrderData = this.mapToIssuePartsData(request);
      this.openIssuePartsModal(IssuePartsMode.Single, workOrderData);
    } else {
      if (this.selectedWorkOrders.size === 0) return;
      const selected = this.displayedRequests.filter(r => this.selectedWorkOrders.has(r.workOrderId));
      const workOrders = selected.map(r => this.mapToIssuePartsData(r));
      this.openIssuePartsModal(IssuePartsMode.Bulk, null, workOrders);
    }
  }

  private mapToIssuePartsData(request: PartRequest): IssuePartsWorkOrderData {
    const approvedParts = (request.parts ?? []).filter(p => p.status === PartRequestStatus.Approved);
    return {
      workOrderId: request.workOrderId,
      workOrderNumber: request.workOrderNumber,
      parts: approvedParts.map(p => ({
        id: p.id,
        partNumber: p.partNumber,
        description: p.category,
        category: p.category,
        onHand: p.quantityAvailable,
        quantityRequested: p.quantityRequested
      }))
    };
  }

  private openIssuePartsModal(mode: IssuePartsMode, workOrder: IssuePartsWorkOrderData | null, workOrders: IssuePartsWorkOrderData[] = []): void {
    const modalRef = this.modalService.open(IssuePartsModalComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'issue-parts-modal'
    });

    modalRef.componentInstance.mode = mode;
    if (mode === IssuePartsMode.Single) {
      modalRef.componentInstance.workOrder = workOrder;
    } else {
      modalRef.componentInstance.workOrders = workOrders;
    }

    const issuedPartIds = (workOrder ? [workOrder] : workOrders).flatMap(wo => wo.parts.map(p => p.id));
    modalRef.closed.subscribe(() => {
      this.updatePartStatuses(issuedPartIds, PartRequestStatus.Issued);
      void this.silentRefreshAsync();
    });
  }

  onSelectWorkOrder(workOrderId: number): void {
    if (this.selectedWorkOrders.has(workOrderId)) {
      this.selectedWorkOrders.delete(workOrderId);
    } else {
      this.selectedWorkOrders.add(workOrderId);
    }
  }

  isWorkOrderSelected(workOrderId: number): boolean {
    return this.selectedWorkOrders.has(workOrderId);
  }

  toggleRow(workOrderId: number, event: MouseEvent): void {
    event.stopPropagation();
    if (this.expandedRows.has(workOrderId)) {
      this.expandedRows.delete(workOrderId);
    } else {
      this.expandedRows.add(workOrderId);
    }
  }

  isRowExpanded(workOrderId: number): boolean {
    return this.expandedRows.has(workOrderId);
  }

  onRemove(request: PartRequest, event: MouseEvent): void {
    event.stopPropagation();
    const rejectableParts = (request.parts ?? []).filter(p =>
      p.status !== PartRequestStatus.Rejected && p.status !== PartRequestStatus.Issued
    );
    if (!rejectableParts.length) return;
    this.openRejectModal(
      request.workOrderNumber,
      rejectableParts.map(p => ({ partNumber: p.partNumber, description: p.category })),
      async (result: RejectPartResult) => {
        await this.rejectPartsAsync(rejectableParts.map(p => p.id), result.note);
      }
    );
  }

  onResetPartStatus(part: PartRequestItem, event: MouseEvent): void {
    event.stopPropagation();
    const state = this.getActionState(part);
    if (state !== PartRequestActionState.Approved && state !== PartRequestActionState.Rejected) return;

    const isApproved = state === PartRequestActionState.Approved;
    void this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate(isApproved ? 'parts.request.reset.titleApprovedPart' : 'parts.request.reset.titleRejectedPart'),
      message: this.transloco.translate('parts.request.reset.messagePart', { partNumber: part.partNumber }),
      confirmText: this.transloco.translate('parts.request.reset.confirmText'),
      declineText: this.transloco.translate('parts.request.reset.declineText'),
      onConfirm: async () => { await this.resetPartsAsync([part.id]); }
    });
  }

  onResetStatus(request: PartRequest, event: MouseEvent): void {
    event.stopPropagation();
    const state = this.getWorkOrderActionState(request);
    if (state !== PartRequestActionState.Approved && state !== PartRequestActionState.Rejected) return;

    const resettableParts = (request.parts ?? []).filter(p =>
      p.status === PartRequestStatus.Approved || p.status === PartRequestStatus.Rejected
    );
    if (!resettableParts.length) return;

    const isApproved = state === PartRequestActionState.Approved;
    void this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate(isApproved ? 'parts.request.reset.titleApprovedParts' : 'parts.request.reset.titleRejectedParts'),
      message: this.transloco.translate(
        isApproved ? 'parts.request.reset.messageApprovedParts' : 'parts.request.reset.messageRejectedParts',
        { workOrderNumber: request.workOrderNumber }
      ),
      confirmText: this.transloco.translate('parts.request.reset.confirmText'),
      declineText: this.transloco.translate('parts.request.reset.declineText'),
      onConfirm: async () => { await this.resetPartsAsync(resettableParts.map(p => p.id)); }
    });
  }

  onApproveAll(request: PartRequest, event: MouseEvent): void {
    event.stopPropagation();
    const approvableParts = (request.parts ?? []).filter(p => p.status === PartRequestStatus.Pending);
    if (!approvableParts.length) return;

    const modalRef = this.modalService.open(ApprovePartsModalComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'approve-parts-modal'
    });

    modalRef.componentInstance.workOrderNumber = request.workOrderNumber;
    modalRef.componentInstance.parts = approvableParts.map(p => ({ partNumber: p.partNumber, category: p.category }));
    modalRef.closed.subscribe(() => this.approvePartsAsync(approvableParts.map(p => p.id)));
  }

  onApprove(part: PartRequestItem, event: MouseEvent): void {
    event.stopPropagation();
    void this.approvePartsAsync([part.id]);
  }

  onReject(request: PartRequest, part: PartRequestItem, event: MouseEvent): void {
    event.stopPropagation();
    this.openRejectModal(
      request.workOrderNumber,
      [{ partNumber: part.partNumber, description: part.category }],
      async (result: RejectPartResult) => {
        await this.rejectPartsAsync([part.id], result.note);
      }
    );
  }

  onViewDetails(part: PartRequestItem, event: MouseEvent): void {
    event.stopPropagation();
    this.openNotesModal(part);
  }

  onAddNote(part: PartRequestItem, event: MouseEvent): void {
    event.stopPropagation();
    this.openNotesModal(part);
  }

  private openNotesModal(part: PartRequestItem): void {
    const request = this.displayedRequests.find(r => r.parts?.some(p => p.id === part.id));
    const ref = this.modalService.open(ViewPartNotesComponent, {
      windowClass: 'view-part-notes-modal-window',
      backdrop: 'static',
      centered: true
    });
    ref.componentInstance.part = part;
    ref.componentInstance.workOrderNumber = request?.workOrderNumber ?? '';
    ref.componentInstance.accountIds = this.widgetFilter.accountIds ?? [];
  }

  getRequestStatusText(request: PartRequest): string {
    return this.getRequestStatusSummary(request).text;
  }

  getRequestStatusVariant(request: PartRequest): PartRequestStatusVariant {
    return this.getRequestStatusSummary(request).variant;
  }

  canIssueParts(request: PartRequest): boolean {
    const parts = request.parts ?? [];
    return parts.some(p => p.status === PartRequestStatus.Approved) &&
      !parts.some(p => p.status === PartRequestStatus.Pending);
  }

  isWorkOrderProcessing(request: PartRequest): boolean {
    return (request.parts ?? []).some(p =>
      this.processingPartIds.has(p.id) || this.rejectingPartIds.has(p.id)
    );
  }

  getWorkOrderActionState(request: PartRequest): PartRequestActionState {
    const parts = request.parts ?? [];
    if (!parts.length) return PartRequestActionState.Pending;

    if (parts.some(p => p.status === PartRequestStatus.Pending)) return PartRequestActionState.Pending;
    if (parts.every(p => p.status === PartRequestStatus.Issued)) return PartRequestActionState.Issued;
    if (parts.every(p => p.status === PartRequestStatus.Approved || p.status === PartRequestStatus.Issued)) return PartRequestActionState.Approved;
    if (parts.every(p => p.status === PartRequestStatus.Rejected || p.status === PartRequestStatus.Issued)) return PartRequestActionState.Rejected;

    return PartRequestActionState.Pending;
  }

  getActionState(part: PartRequestItem): PartRequestActionState {
    switch (part.status) {
      case PartRequestStatus.Approved:
        return PartRequestActionState.Approved;
      case PartRequestStatus.Issued:
        return PartRequestActionState.Issued;
      case PartRequestStatus.Rejected:
        return PartRequestActionState.Rejected;
      default:
        return PartRequestActionState.Pending;
    }
  }

  getWorkOrderTooltip(value: string | null | undefined): string | null {
    return this.shouldShowTooltip(value, this.workOrderTooltipThreshold) ? value! : null;
  }

  getAssetTooltip(value: string | null | undefined): string | null {
    return this.shouldShowTooltip(value, this.assetTooltipThreshold) ? value! : null;
  }

  getRequestedByTooltip(value: string | null | undefined): string | null {
    return this.shouldShowTooltip(value, this.requestedByTooltipThreshold) ? value! : null;
  }

  getPartNumberTooltip(value: string | null | undefined): string | null {
    return this.shouldShowTooltip(value, this.partNumberTooltipThreshold) ? value! : null;
  }

  getPartNameTooltip(value: string | null | undefined): string | null {
    return this.shouldShowTooltip(value, this.partNameTooltipThreshold) ? value! : null;
  }

  getCategoryTooltip(value: string | null | undefined): string | null {
    return this.shouldShowTooltip(value, this.categoryTooltipThreshold) ? value! : null;
  }

  trackByWorkOrderId(_: number, request: PartRequest): number {
    return request.workOrderId;
  }

  trackByPartRequestId(_: number, part: PartRequestItem): number {
    return part.id;
  }

  private openRejectModal(
    workOrderNumber: string,
    parts: { partNumber: string; description?: string }[],
    onSave: (result: RejectPartResult) => Promise<void>
  ): void {
    const ref = this.modalService.open(RejectPartComponent, {
      windowClass: 'reject-part-modal-window',
      backdrop: 'static',
      centered: true
    });
    ref.componentInstance.workOrderNumber = workOrderNumber;
    ref.componentInstance.parts = parts;
    ref.componentInstance.onSave = onSave;
  }

  private async rejectPartsAsync(partRequestIds: number[], note: string | null): Promise<void> {
    partRequestIds.forEach(id => this.rejectingPartIds.add(id));
    try {
      await this.partsRequestService.rejectWorkOrderPartsAsync({ partRequestIds, note });
      this.updatePartStatuses(partRequestIds, PartRequestStatus.Rejected);
      this.toastr.success(this.transloco.translate('parts.request.toasts.rejected'));
      void this.silentRefreshAsync();
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      partRequestIds.forEach(id => this.rejectingPartIds.delete(id));
    }
  }

  private async approvePartsAsync(partRequestIds: number[]): Promise<void> {
    partRequestIds.forEach(id => this.processingPartIds.add(id));
    try {
      await this.partsRequestService.approveWorkOrderPartsAsync({ partRequestIds });
      this.updatePartStatuses(partRequestIds, PartRequestStatus.Approved);
      this.toastr.success(this.transloco.translate('parts.request.toasts.approved'));
      void this.silentRefreshAsync();
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      partRequestIds.forEach(id => this.processingPartIds.delete(id));
    }
  }

  private async resetPartsAsync(partRequestIds: number[]): Promise<void> {
    partRequestIds.forEach(id => this.processingPartIds.add(id));
    try {
      await this.partsRequestService.resetWorkOrderPartsAsync({ partRequestIds });
      this.updatePartStatuses(partRequestIds, PartRequestStatus.Pending);
      this.toastr.success(this.transloco.translate('parts.request.toasts.reset'));
      void this.silentRefreshAsync();
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      partRequestIds.forEach(id => this.processingPartIds.delete(id));
    }
  }

  private updatePartStatuses(partIds: number[], status: PartRequestStatus): void {
    this.displayedRequests = this.displayedRequests.map(request => ({
      ...request,
      parts: request.parts?.map(part =>
        partIds.includes(part.id) ? { ...part, status } : part
      ) ?? []
    }));
  }

  private async silentRefreshAsync(): Promise<void> {
    this.refreshCancellation.next();

    this.isRefreshing = true;
    try {
      const pagesToFetch = Math.max(1, Math.ceil(this.displayedRequests.length / this.pageSize));
      const params = {
        pageSize: this.pageSize,
        searchTerm: this.searchTerm.trim(),
        accountIds: this.widgetFilter.accountIds ?? [],
        dateFrom: this.formatRequestDate(this.widgetFilter.startDate),
        dateTo: this.formatRequestDate(this.widgetFilter.endDate),
        status: this.getSelectedStatusId(),
        sortColumn: this.sortColumn ?? undefined,
        sortDirection: this.sortDirection ?? undefined
      };

      const responses = await Promise.all(
        Array.from({ length: pagesToFetch }, (_, i) =>
          this.partsRequestService.getPartsRequestsAsync({ ...params, page: i + 1 }, this.refreshCancellation)
        )
      );

      const results = responses.flatMap(r => r.data?.results ?? []);
      if (results.length) {
        this.displayedRequests = results;
      }
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    } finally {
      this.isRefreshing = false;
    }
  }

  private async resetAndLoadRequestsAsync(): Promise<void> {
    this.currentPage = 1;
    this.totalPages = 0;
    this.hasMoreData = true;
    this.displayedRequests = [];
    this.expandedRows.clear();
    this.selectedWorkOrders.clear();

    await this.loadRequestsPageAsync(true);
  }

  private async loadRequestsPageAsync(isInitialLoad: boolean): Promise<void> {
    if (!isInitialLoad && (!this.hasMoreData || this.isLoadingMore)) {
      return;
    }

    this.loadCancellation.next();

    const requestId = ++this.requestSequence;

    if (isInitialLoad) {
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
    }

    this.hasError = false;

    try {
      const request: PartsRequestFilterRequest = {
        page: this.currentPage,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm.trim(),
        accountIds: this.widgetFilter.accountIds ?? [],
        dateFrom: this.formatRequestDate(this.widgetFilter.startDate),
        dateTo: this.formatRequestDate(this.widgetFilter.endDate),
        status: this.getSelectedStatusId(),
        sortColumn: this.sortColumn ?? undefined,
        sortDirection: this.sortDirection ?? undefined
      };

      const response = await this.partsRequestService.getPartsRequestsAsync(request, this.loadCancellation);

      if (requestId !== this.requestSequence) {
        return;
      }

      const results = response.data?.results ?? [];
      this.displayedRequests = isInitialLoad ? results : [...this.displayedRequests, ...results];
      this.totalPages = response.data?.totalPages ?? 0;
      this.hasMoreData = this.currentPage < this.totalPages;

      if (this.hasMoreData) {
        this.currentPage += 1;
      }
    } catch (error: any) {
      if (requestId === this.requestSequence) {
        this.errorHandlerService.handleError(error, {
          callback: () => {
            this.hasError = true;
          }
        });
      }
    } finally {
      if (requestId === this.requestSequence) {
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    }
  }

  private getSelectedStatusId(): PartRequestStatus | null {
    switch (this.selectedCategory) {
      case 'Pending':
        return PartRequestStatus.Pending;
      case 'Approved':
        return PartRequestStatus.Approved;
      case 'Rejected':
        return PartRequestStatus.Rejected;
      default:
        return null;
    }
  }

  private getRequestStatusSummary(request: PartRequest): RequestStatusSummary {
    const parts = request.parts ?? [];
    const requestCount = parts.length || request.quantity || 0;

    if (requestCount === 0) {
      return {
        text: this.transloco.translate('parts.request.statusSummary.all', {
          total: 0,
          label: this.transloco.translate('parts.request.statusLabels.draft')
        }),
        variant: 'pending',
        category: 'Draft'
      };
    }

    const rejectedCount = parts.filter(part => part.status === PartRequestStatus.Rejected).length;
    const pendingCount = parts.filter(part =>
      part.status === PartRequestStatus.Pending || part.status === PartRequestStatus.Draft
    ).length;
    const issuedCount = parts.filter(part => part.status === PartRequestStatus.Issued).length;
    const approvedCount = parts.filter(part => part.status === PartRequestStatus.Approved).length;

    if (rejectedCount > 0) {
      return this.buildSummary(rejectedCount, requestCount, this.transloco.translate('parts.request.statusLabels.rejected'), 'rejected', 'Rejected');
    }

    if (pendingCount > 0) {
      return this.buildSummary(pendingCount, requestCount, this.transloco.translate('parts.request.statusLabels.pendingApproval'), 'pending', 'Pending');
    }

    if (issuedCount > 0) {
      return this.buildSummary(issuedCount, requestCount, this.transloco.translate('parts.request.statusLabels.issued'), 'approved', 'Issued');
    }

    if (approvedCount > 0) {
      return this.buildSummary(approvedCount, requestCount, this.transloco.translate('parts.request.statusLabels.approved'), 'approved', 'Approved');
    }

    return this.buildSummary(requestCount, requestCount, this.transloco.translate('parts.request.statusLabels.draft'), 'pending', 'Draft');
  }

  private buildSummary(
    count: number,
    total: number,
    label: string,
    variant: PartRequestStatusVariant,
    category: RequestStatusSummary['category']
  ): RequestStatusSummary {
    return {
      text: count === total
        ? this.transloco.translate('parts.request.statusSummary.all', { total, label })
        : this.transloco.translate('parts.request.statusSummary.partial', { count, total, label }),
      variant,
      category
    };
  }

  private shouldShowTooltip(value: string | null | undefined, threshold: number): boolean {
    return !!value && value.trim().length > threshold;
  }

  private formatRequestDate(value: Date | null | undefined): string | null {
    if (!value) {
      return null;
    }

    return format(value, "yyyy-MM-dd'T'HH:mm:ss");
  }
}